import { useState } from "react";
import { Briefcase, Pencil, Trash2, UsersRound } from "lucide-react";

import { cn } from "@/lib/utils";
import { ButtonShadcn } from "@/components/ui/Button";
import { SegmentedControl } from "@/components/ui/SegmentedControl";
import { Textarea } from "@/components/ui/Textarea";
import { formatExactDateTime, formatRelativeTime } from "@/utils/dateUtils";
import { getInitials as initials } from "@/utils/formatText";
import { NOTE_BODY_MAX_LENGTH, noteSchema } from "../noteSchema";
import { LEGEND_MOVE, SCOPE_LABEL, scopeMarker, scopeOf, THIS_JOB, EVERY_JOB } from "../scope";

/**
 * One note in the stream.
 *
 * A row, not a card — the stream itself is already a card (see `Notes.jsx`),
 * so a stack of bordered boxes inside a bordered box is the thing that made
 * this screen read as generated. Rows share one hairline between them and
 * pick up a soft highlight on hover so the row a reader is looking at is
 * never ambiguous, without giving every row a border of its own.
 *
 * **The scope is a marker in the metadata row, beside the author and the time.**
 * It used to be a sentence under the body — "Shows on all their jobs" — which
 * put a line of prose where the eye expects more note, and read as writing
 * rather than as a fact about the writing. A short label with an icon sits where
 * metadata belongs and costs the row no height. The two icons distinguish the
 * scopes without colour, and the sentence behind them is in the `title`.
 *
 * This component decides nothing about who may do what. The tab reads the flags
 * off `useAuth().user` and the note's own author and passes them down — one
 * place holding the rules of §3, rather than two components each holding half.
 *
 * Editing happens in place rather than in a modal: a note is a paragraph, and
 * the words around it are the context you are editing against. It opens into
 * its own recessed panel — the same "its own ground" idea as the composer —
 * so mid-edit is visually distinct from the plain-text row above it. The
 * editor stays open until the save comes back, and closes only on success —
 * closing on the click threw away the reworded note whenever the request
 * failed, and the toast arrived with nothing left to retype.
 *
 * **The scope is editable here, not only at the keyboard.** Realising a warning
 * is stuck on one job is a thing that happens after posting, and before this the
 * only way back was deleting the note and typing it again under a different
 * choice. Save is live when the body changed **or** the scope did.
 *
 * The scope control targets `note.applicationId ?? applicationId` — the job the
 * note already sits on, or the one the screen is open on. It never moves a note
 * from one job to another: those are the only two ids in play, and with the
 * stream scoped as it is (§1) a note you can see from job A is either A's or
 * nobody's.
 *
 * The time is relative on screen and exact in the `title`, because a relative
 * time is easier to read and impossible to check.
 *
 * @param {object} note                a `NoteItem` from the server, camelCase
 * @param {string|null} [applicationId] the application the screen was opened on
 * @param {boolean} [canEdit]          the reader wrote this note and may reword it
 * @param {boolean} [canDelete]        the reader wrote this note and may remove it
 * @param {boolean} [busy]             a request for this note is in flight
 * @param {Function} onEdit            (note, {body, applicationId}) — body already trimmed; rejects when the save failed
 * @param {Function} onDelete          (note)
 */
const NoteRow = ({ note, applicationId = null, canEdit = false, canDelete = false, busy = false, onEdit, onDelete }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(note.body);
  const [draftScope, setDraftScope] = useState(scopeOf(note));
  const [draftError, setDraftError] = useState(null);

  // The job this note would go onto if the reader picks "This job": the one it
  // already sits on, or the one the screen is open on. With neither there is no
  // second option, so the control does not render.
  const targetApplicationId = note.applicationId ?? applicationId;
  const jobLabel = note.applicationId ? note.jobTitle : "";

  const marker = scopeMarker(note);
  const ScopeIcon = scopeOf(note) === THIS_JOB ? Briefcase : UsersRound;

  // The same rule as the composer, and it runs on every keystroke so Save is
  // never pressable while the message is showing.
  const validate = (value) => {
    const result = noteSchema.safeParse({ body: value });
    setDraftError(result.success ? null : result.error.issues[0]?.message || "A note cannot be empty.");
    return result;
  };

  const openEditor = () => {
    setDraft(note.body);
    setDraftScope(scopeOf(note));
    setDraftError(null);
    setEditing(true);
  };

  const closeEditor = () => {
    setDraft(note.body);
    setDraftScope(scopeOf(note));
    setDraftError(null);
    setEditing(false);
  };

  const save = async () => {
    const result = validate(draft);
    if (!result.success) return;
    try {
      await onEdit?.(note, {
        body: result.data.body,
        applicationId: draftScope === THIS_JOB ? targetApplicationId : null,
      });
      setEditing(false);
      setDraftError(null);
    } catch {
      // The owner reported it. Keep the editor open on the reworded text so
      // the words are still there to retry with.
    }
  };

  const unchanged = draft.trim() === (note.body ?? "").trim() && draftScope === scopeOf(note);
  const saveDisabled = busy || Boolean(draftError) || draft.trim().length === 0 || unchanged;

  const scopeOptions = [
    { value: THIS_JOB, label: SCOPE_LABEL[THIS_JOB], title: jobLabel || undefined },
    { value: EVERY_JOB, label: SCOPE_LABEL[EVERY_JOB] },
  ];

  return (
    <article
      className={cn(
        "group flex gap-3 rounded-xl px-3 py-3 transition-colors",
        !editing && "hover:bg-background-tertiary/40",
        busy && "opacity-50",
      )}
    >
      <span
        aria-hidden="true"
        className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-background-tertiary text-[11px] font-semibold text-text-secondary ring-1 ring-border-primary/50"
      >
        {initials(note.author?.name)}
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-sm font-medium leading-6 text-text-primary">{note.author?.name || "Unknown user"}</span>
            <span className="text-xs text-text-secondary" title={formatExactDateTime(note.createdAt) || undefined}>
              {formatRelativeTime(note.createdAt)}
            </span>
            {note.editedAt && (
              <span className="text-xs text-text-muted" title={`Edited ${formatExactDateTime(note.editedAt)}`}>
                edited
              </span>
            )}
            <span
              data-testid="note-scope"
              title={marker.hint}
              className="inline-flex max-w-[240px] items-center gap-1 rounded-full border border-border-primary/60 bg-background-tertiary/60 px-2 py-0.5 text-[11px] font-medium text-text-secondary"
            >
              <ScopeIcon className="h-3 w-3 shrink-0" aria-hidden="true" />
              <span className="truncate">{marker.label}</span>
            </span>
          </div>

          {(canEdit || canDelete) && !editing && (
            // Rendered always, not on hover: a control that only exists once
            // the pointer is over it cannot be found on a touch screen. It is
            // simply quiet until the row is hovered or something in it takes
            // the keyboard, and that brightening is the whole affordance.
            <div className="flex shrink-0 items-center gap-0.5 opacity-60 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
              {canEdit && (
                <ButtonShadcn
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Edit note"
                  title="Edit note"
                  disabled={busy}
                  className="rounded-full text-text-secondary hover:bg-background-tertiary hover:text-text-primary"
                  onClick={openEditor}
                >
                  <Pencil aria-hidden="true" />
                </ButtonShadcn>
              )}
              {canDelete && (
                <ButtonShadcn
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Delete note"
                  title="Delete note"
                  disabled={busy}
                  className="rounded-full text-text-secondary hover:bg-background-tertiary hover:text-status-error"
                  onClick={() => onDelete?.(note)}
                >
                  <Trash2 aria-hidden="true" />
                </ButtonShadcn>
              )}
            </div>
          )}
        </div>

        {editing ? (
          <div className="mt-2 rounded-xl border border-border-primary bg-background-secondary/60 p-3">
            <label htmlFor={`note-edit-${note.id}`} className="sr-only">
              Edit your note
            </label>
            <Textarea
              id={`note-edit-${note.id}`}
              aria-invalid={draftError ? true : undefined}
              aria-describedby={draftError ? `note-edit-error-${note.id}` : undefined}
              rows={4}
              value={draft}
              maxLength={NOTE_BODY_MAX_LENGTH}
              disabled={busy}
              className={cn("rounded-lg bg-background-primary leading-relaxed", draftError && "border-status-error")}
              onChange={(event) => {
                setDraft(event.target.value);
                validate(event.target.value);
              }}
            />

            {draftError && (
              <p id={`note-edit-error-${note.id}`} className="mt-1.5 text-xs text-status-error">
                {draftError}
              </p>
            )}

            <div className="mt-3 flex flex-wrap items-center justify-end gap-x-4 gap-y-3 border-t border-border-primary/70 pt-3">
              {targetApplicationId && (
                <div className="mr-auto flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
                  <span className="text-xs font-medium text-text-secondary">{LEGEND_MOVE}</span>
                  <SegmentedControl options={scopeOptions} value={draftScope} onChange={setDraftScope} disabled={busy} ariaLabel={LEGEND_MOVE} />
                </div>
              )}

              {/* The same pair the rest of the app uses: the filled accent for
                  the act, `button-tertiary` for the way out. Save matches Add
                  note exactly, because they do the same thing to the same
                  words. */}
              <div className="flex shrink-0 items-center gap-2">
                <ButtonShadcn type="button" variant="tertiary" size="lg" disabled={busy} onClick={closeEditor}>
                  Cancel
                </ButtonShadcn>
                <ButtonShadcn
                  type="button"
                  variant="secondary"
                  size="lg"
                  disabled={saveDisabled}
                  title={draftError || (unchanged ? "Nothing has changed yet." : undefined)}
                  onClick={save}
                >
                  {busy ? "Saving…" : "Save"}
                </ButtonShadcn>
              </div>
            </div>
          </div>
        ) : (
          // Plain text, and rendered as plain text on purpose — no markdown,
          // no autolinking.
          // `whitespace-pre-wrap` keeps the newlines the author typed; React
          // escapes the rest.
          <p data-testid="note-body" className="mt-1 whitespace-pre-wrap break-words text-sm leading-relaxed text-text-primary">
            {note.body}
          </p>
        )}
      </div>
    </article>
  );
};

export default NoteRow;